import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lovable-demo/runtime/client";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";
import type { Rpc } from "@/lovable-demo/lib/warranty/pfmea-signoff";

/** The exported closure policy requires an independent director after the request. */
export function ClosureReview({ record }: { record: ProblemRecord }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { refreshRecord } = useWarranty();
  const cache = useQueryClient();
  const request = useQuery({
    queryKey: ["lovable-demo", "closure-review", record.id],
    queryFn: async () => {
      const response = await supabase
        .from("audit_events")
        .select("*")
        .eq("entity_id", record.id)
        .eq("event_type", "demo.closure_requested")
        .order("occurred_at", { ascending: false })
        .limit(1);
      if (response.error) throw response.error;
      return response.data?.[0] ?? null;
    },
  });
  return (
    <section
      aria-label="Simulated closure review"
      className="space-y-2 rounded border border-border p-3 text-xs"
    >
      <p>
        Closure needs a separate simulated Quality Director or Administrator. Complete the final
        evidence and 8D gates, request review, then switch to that reviewer and complete Stage 11.
        Any later record change requires a fresh request.
      </p>
      {request.data && (
        <p role="status">
          Review requested by {request.data.actor_label}. A different authorized persona must
          complete closure.
        </p>
      )}
      <label className="block">
        Closure review rationale
        <Input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="mt-1"
        />
      </label>
      <Button
        size="sm"
        variant="outline"
        disabled={busy || reason.trim().length < 10}
        onClick={async () => {
          setBusy(true);
          try {
            const response = await (supabase.rpc as unknown as Rpc)("demo_request_closure", {
              p_record_id: record.id,
              p_rationale: reason,
            });
            if (response.error) throw new Error(response.error.message);
            await refreshRecord(record.id);
            await cache.invalidateQueries({
              queryKey: ["lovable-demo", "closure-review", record.id],
            });
            setReason("");
            toast.success("Simulated closure review requested");
          } catch (error) {
            toast.error("Closure review not requested", {
              description: error instanceof Error ? error.message : "Local action failed.",
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        Request closure review
      </Button>
    </section>
  );
}
